import {
	SaveUserProfileModel,
	UserIdentityModel,
	UserProfileCustomStringViewModel,
	UserProfileModel,
	UserPronounKind,
	saveUserProfile
} from "client/Api/UserProfile/service";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	NavigationCallback,
	PredefinedPropOptionKind,
	connectToRouter
} from "client/Navigation";
import {
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	createValidationConfiguration
} from "client/Validation";
import {
	doesNotExceedLength,
	isValidDate,
	isEmailAddress,
	isNotNullNorUndefined,
	hasMaximumEntries
} from "client/Validation/rules";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import { MAX_TAG_COUNT } from "./UserTagSelect";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { useLoadUserProfileViewModel } from "./useLoadUserProfileViewModel";
import { useUserProfileStore } from "./userProfileStore";
import { getCurrentCulture, localize } from "client_legacy/ndaw/culture";
import { userProfileRouting } from "./UserProfilePage";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { ActionKind, buildActionsForTopBar } from "client_legacy/ndaw/actions";
import { isNotNil } from "client/Misc/utils";
import { UserProfileForm } from "./UserProfileForm";
import { useActiveSettingsMenuItem } from "../SidebarMenu/sidebarMenuSelectionStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { parseDtoDate } from "client/DateAndTime/dateTimeUtils";
import {
	TimeZone,
	getUtcTimeZone,
	tryGetTimeZone
} from "client/DateAndTime/timezones";
import { convertToDateOnly } from "client/DateAndTime/dateUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { useEditRestrictionsMoreInfo } from "./useEditRestrictionsMoreInfo";
import { SettingsPage } from "../SettingsPage";
import { ToolbarAction } from "client/Components";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";
import { PathRedirect } from "client/Validation/serverErrors";

export type UserProfileEditProps = {
	onNavigate: NavigationCallback;
};

export const UserProfileEdit = (props: UserProfileEditProps): ReactElement => {
	const {
		onNavigate
	} = props;

	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [formData, setFormData] = useState<UserProfileFormData>(DEFAULT_FORM_DATA);
	const [resetFormData, setResetFormData] = useState<UserProfileFormData>(DEFAULT_FORM_DATA);

	const {
		viewModel,
		status
	} = useLoadUserProfileViewModel();

	const setCurrentUserIdentity: (patch: Partial<Omit<UserIdentityModel, "userId">>) => void = useUserProfileStore(x => x.setCurrentUserIdentity);
	const setUserTimezone: (timeZone: TimeZone | null) => void = useUserProfileStore(x => x.setUserTimezone);
	useActiveSettingsMenuItem(SettingsPageKind.UserProfile);

	useEffect(
		(): void => {
			if (!viewModel) {
				return;
			}

			const saveModel: UserProfileFormData = convertToUserProfileFormData(viewModel.model);

			setFormData(saveModel);
			setResetFormData(saveModel);
		},
		[viewModel]
	);

	const formHandler: IFormHandler<UserProfileFormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const [moreInfoHandler, moreInfoElement] = useEditRestrictionsMoreInfo(viewModel?.editRestrictions);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const saveFormHandler = useCallback(
		async (): Promise<void> => {
			if (status !== "ready" || isSaving) {
				return;
			}

			try {
				setIsSaving(true);

				const validationResult: InputValidationResult = await formHandler.validation
					.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsSaving(false);
					return;
				}

				const model: SaveUserProfileModel = prepareForSaving(formData);
				await saveUserProfile(model, globalCancellation);

				setResetFormData(formData);

				setCurrentUserIdentity({
					firstName: model.firstName,
					lastName: model.lastName,
					jobTitle: model.jobTitle
				});

				if (isNotNil(model.timeZone)) {
					const timezoneRetrieval: TryResult<TimeZone> = tryGetTimeZone(model.timeZone, getCurrentCulture());
					if (timezoneRetrieval.wasSuccessful) {
						setUserTimezone(timezoneRetrieval.result);
					} else {
						console.error(`Unable to retrieve information about user's timezone.`);
					}
				}

				alertManager.showSuccess(localize("_ChangesSaved"));

				setIsSaving(false);
				onNavigate(userProfileRouting.page);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setIsSaving(false);

				if (
					formHandler
						.validation
						.tryProcessServerError(e, serverPathRedirects)
				) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				console.error(e);
			}
		},
		[
			status,
			isSaving,
			formHandler.validation,
			globalCancellation,
			formData,
			setCurrentUserIdentity,
			setUserTimezone,
			alertManager,
			onNavigate
		]
	);

	const backToOverviewHandler = useCallback(
		(): void => {
			onNavigate(userProfileRouting.page);
		},
		[onNavigate]
	);

	const cancelFormHandler = useCallback(
		(): void => {
			setFormData(resetFormData);

			formHandler.validation
				.validate();

			backToOverviewHandler();
		},
		[
			resetFormData,
			formHandler.validation,
			backToOverviewHandler
		]
	);

	const defaultActions: readonly EditUserProfileAction[] = useMemo(
		() => {
			return listDefaultActions({
				saveFormHandler,
				cancelFormHandler
			});
		},
		[
			cancelFormHandler,
			saveFormHandler
		]
	);

	const topBarActions = useMemo(
		(): readonly EditUserProfileAction[] => {
			return buildActionsForTopBar<undefined, EditUserProfileAction>(
				{
					defaultActions,
				},
				false,
				isResponsive,
				false
			);
		},
		[
			defaultActions,
			isResponsive
		]
	);

	return (
		<SettingsPage
			hasBusyIndicator={status === "loading" || isSaving}
			onBack={backToOverviewHandler}
			title={localize("_UserProfile")}
			onMoreInfo={moreInfoHandler}
			actions={topBarActions}
			actionContext={undefined}
		>
			<SettingsPage.Section>
				<UserProfileForm
					formHandler={formHandler}
					editRestrictions={viewModel?.editRestrictions}
					emailAddress={viewModel?.model.emailAddress ?? undefined}
					saveHandler={saveFormHandler}
					birthdayTimezone={birthdayTimezone}
				/>
			</SettingsPage.Section>

			{moreInfoElement}

			<AiAssistantFabSpacing />
		</SettingsPage>
	);
};
UserProfileEdit.displayName = "UserProfileEdit";

const serverPathRedirects: readonly PathRedirect[] = [
	{
		pattern: "CustomString1.Value",
		replacement: "CustomString1.Value.Value"
	},
	{
		pattern: "CustomString2.Value",
		replacement: "CustomString2.Value.Value"
	},
	{
		pattern: "CustomString3.Value",
		replacement: "CustomString3.Value.Value"
	},
	{
		pattern: "CustomString4.Value",
		replacement: "CustomString4.Value.Value"
	},
	{
		pattern: "CustomString5.Value",
		replacement: "CustomString5.Value.Value"
	}
];

export const UserProfileEditForRouter = connectToRouter(
	UserProfileEdit,
	{
		"onNavigate": PredefinedPropOptionKind.NavigateCallback
	}
);

export type CustomStringFormData = {
	displayName: string;
	value: string;
};

export type UserProfileFormData = OverrideProperties<
	SaveUserProfileModel,
	{
		customString1: OptionalModel<CustomStringFormData>;
		customString2: OptionalModel<CustomStringFormData>;
		customString3: OptionalModel<CustomStringFormData>;
		customString4: OptionalModel<CustomStringFormData>;
		customString5: OptionalModel<CustomStringFormData>;
	}
>;

const DEFAULT_FORM_DATA: UserProfileFormData = {
	firstName: null,
	lastName: null,
	jobTitle: null,
	department: null,
	officeLocation: null,
	secondaryEmailAddress: null,
	phoneNumber: null,
	birthday: null,
	customString1: { isSet: false },
	customString2: { isSet: false },
	customString3: { isSet: false },
	customString4: { isSet: false },
	customString5: { isSet: false },
	aboutMe: null,
	projects: [],
	education: [],
	hobbies: [],
	skills: [],
	timeZone: null,
	hasTimeZoneAutomaticallySet: true,
	workingDays: {
		monday: false,
		tuesday: false,
		wednesday: false,
		thursday: false,
		friday: false,
		saturday: false,
		sunday: false
	},
	pronouns: UserPronounKind.None
};

function convertToUserProfileFormData(model: UserProfileModel): UserProfileFormData {
	return {
		firstName: model.firstName ?? "",
		lastName: model.lastName ?? "",
		jobTitle: model.jobTitle ?? "",
		department: model.department,
		officeLocation: model.officeLocation,
		secondaryEmailAddress: model.secondaryEmailAddress ?? "",
		phoneNumber: model.phoneNumber ?? "",
		birthday: parseDtoDate(model.birthday),
		customString1: convertCustomStringToFormData(model.customString1),
		customString2: convertCustomStringToFormData(model.customString2),
		customString3: convertCustomStringToFormData(model.customString3),
		customString4: convertCustomStringToFormData(model.customString4),
		customString5: convertCustomStringToFormData(model.customString5),
		aboutMe: model.aboutMe ?? "",
		projects: model.projects,
		education: model.education,
		hobbies: model.hobbies,
		skills: model.skills,
		workingDays: model.workingDays,
		timeZone: model.timeZone,
		hasTimeZoneAutomaticallySet: model.hasTimeZoneAutomaticallySet,
		pronouns: model.pronouns
	};
};

function convertCustomStringToFormData(
	customString: OptionalModel<UserProfileCustomStringViewModel>
): OptionalModel<CustomStringFormData> {
	if (!customString.isSet) {
		return { isSet: false };
	}

	const {
		displayName,
		value
	} = customString.value;

	return {
		isSet: true,
		value: {
			displayName,
			value: value ?? ""
		}
	};
}

function convertToSaveUserProfileModel(model: UserProfileFormData): SaveUserProfileModel {
	return {
		firstName: model.firstName ?? "",
		lastName: model.lastName ?? "",
		jobTitle: model.jobTitle ?? "",
		department: model.department,
		officeLocation: model.officeLocation,
		secondaryEmailAddress: model.secondaryEmailAddress ?? "",
		phoneNumber: model.phoneNumber ?? "",
		birthday: parseDtoDate(model.birthday),
		customString1: convertCustomStringToSaveModel(model.customString1),
		customString2: convertCustomStringToSaveModel(model.customString2),
		customString3: convertCustomStringToSaveModel(model.customString3),
		customString4: convertCustomStringToSaveModel(model.customString4),
		customString5: convertCustomStringToSaveModel(model.customString5),
		aboutMe: model.aboutMe ?? "",
		projects: model.projects,
		education: model.education,
		hobbies: model.hobbies,
		skills: model.skills,
		workingDays: model.workingDays,
		timeZone: model.timeZone,
		hasTimeZoneAutomaticallySet: model.hasTimeZoneAutomaticallySet,
		pronouns: model.pronouns
	};
};

function convertCustomStringToSaveModel(
	customString: OptionalModel<CustomStringFormData>
): OptionalModel<string> {
	return customString.isSet
		? {
			isSet: true,
			value: customString
				.value
				.value
		}
		: { isSet: false };
}

const birthdayTimezone: TimeZone = getUtcTimeZone(getCurrentCulture());

function prepareForSaving(
	model: UserProfileFormData
): SaveUserProfileModel {
	const result: SaveUserProfileModel = {
		...convertToSaveUserProfileModel(model)
	};

	if (isNotNil(result.birthday)) {
		const { birthday } = result;

		const birthDayDate: DateOnly = convertToDateOnly(
			birthday,
			birthdayTimezone
		);

		result.birthday = new Date(
			Date.UTC(
				birthDayDate.year,
				birthDayDate.monthIndex,
				birthDayDate.day
			)
		);
	}

	if (model.hasTimeZoneAutomaticallySet) {
		result.timeZone = null;
	}

	return result;
}

const validationConfiguration: IValidationConfiguration<UserProfileFormData> =
	createValidationConfiguration<UserProfileFormData>();

validationConfiguration.forScalar("department")
	.add(
		doesNotExceedLength(64)
	);

validationConfiguration.forScalar("officeLocation")
	.add(
		doesNotExceedLength(128)
	);

validationConfiguration.forScalar("secondaryEmailAddress")
	.add(
		isEmailAddress()
	);

validationConfiguration.forScalar("birthday")
	.add(
		isValidDate()
	);

validationConfiguration.forScalar("aboutMe")
	.add(
		doesNotExceedLength(400)
	);

validationConfiguration.forScalar("phoneNumber")
	.add(
		doesNotExceedLength(255)
	);

validationConfiguration.forCollection("projects")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

validationConfiguration.forCollection("education")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

validationConfiguration.forCollection("hobbies")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

validationConfiguration.forCollection("skills")
	.add(
		isNotNullNorUndefined(),
		hasMaximumEntries(MAX_TAG_COUNT)
	);

validationConfiguration
	.forObject("customString1")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("customString2")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("customString3")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("customString4")
	.addValidators(customStringValidator());
validationConfiguration
	.forObject("customString5")
	.addValidators(customStringValidator());

function customStringValidator(): IValidationConfiguration<OptionalModel<CustomStringFormData>> {
	const result: IValidationConfiguration<OptionalModel<CustomStringFormData>> =
		createValidationConfiguration<OptionalModel<CustomStringFormData>>();

	result
		.whenType(x => x.isSet)
		.forObject("value")
		.forScalar("value")
		.add(
			doesNotExceedLength(255)
		);

	return result;
}

type EditUserProfileAction = ToolbarAction<undefined>;

type DefaultActionsHandlers = {
	saveFormHandler(): void;
	cancelFormHandler(): void;
};

function listDefaultActions(
	handlers: DefaultActionsHandlers
): readonly EditUserProfileAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "save-form",
			title: localize("_Save"),
			canExecute: () => true,
			execute: () => handlers.saveFormHandler(),
			icon: "floppy-disk",
			isPrimary: true
		},
		{
			kind: ActionKind.Regular,
			name: "cancel-form",
			title: localize("_Cancel"),
			canExecute: () => true,
			execute: () => handlers.cancelFormHandler(),
			icon: "xmark"
		}
	];
}